<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div>
      <div class="row">
        <div class="col-md-12">
          <h3><i class="fa fa-fw fa-wand-magic-sparkles"></i> {{ _('Johku Max') }}</h3>
          <p class="text-muted">{{ _('Audits and reports that Johku has prepared for your shop.') }}</p>
        </div>
      </div>
      <div class="row">
        <div class="col-md-4 col-sm-6 filtercol">
          <div class="form-group">
            <div class="input-group">
              <div class="input-group-addon">{{ _('Display') }}</div>
              <select class="form-control" v-model="status">
                <option value="active">{{ _('Current') }}</option>
                <option value="new">{{ _('Unread') }}</option>
                <option value="dismissed">{{ _('Dismissed') }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      <p v-if="loading && !artifacts.length" class="text-muted"><i class="fa fa-fw fa-cog fa-spin"></i> {{ _('Loading...') }}</p>
      <p v-else-if="!artifacts.length" class="text-muted">{{ emptyText }}</p>
      <div v-else class="list-group max-artifact-list">
        <button
          v-for="artifact in artifacts"
          :key="artifact.id"
          type="button"
          class="list-group-item max-artifact-row"
          :class="{ 'max-artifact-row--unread': artifact.status === 'new' }"
          v-on:click="openArtifact(artifact)"
        >
          <span class="max-artifact-row__head">
            <strong class="max-artifact-row__title">{{ artifact.title }}</strong>
            <span v-if="artifact.status === 'new'" class="label label-primary">{{ maxLabels.artifact.unread }}</span>
          </span>
          <span class="max-artifact-row__meta text-muted">{{ kindLabel(artifact.kind) }} · {{ $filters.timestampToDate(artifact.generated_ts) }}</span>
          <span v-if="artifact.summary" class="max-artifact-row__summary">{{ artifact.summary }}</span>
          <span v-if="severityChips(artifact).length" class="max-artifact-row__severity">
            <span v-for="chip in severityChips(artifact)" :key="chip.severity" class="max-artifact-chip">
              <span class="max-artifact-chip__dot" :style="{ background: chip.color }" aria-hidden="true"></span>{{ chip.label }} {{ chip.count }}
            </span>
          </span>
        </button>
      </div>
      <button v-if="hasMore" type="button" class="btn btn-default" :disabled="loading" v-on:click="loadMore">{{ _('Show more') }}</button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import components from '~/lib/components'
import maxlabels from '~/lib/maxlabels'
import { SEVERITIES, chartTheme } from '#max-core'

const PAGE_SIZE = 20

export default {
  data () {
    return {
      artifacts: [],
      status: 'active',
      hasMore: false
    }
  },
  mixins: [components, maxlabels],
  computed: {
    emptyText () {
      if (this.status === 'dismissed') {
        return this._('You have not dismissed anything.')
      }
      if (this.status === 'new') {
        return this._('You have read everything.')
      }
      return this._('Nothing here yet. Audits and reports from Johku appear here when they are ready.')
    },
    severityColors () {
      return chartTheme('pos').severities
    }
  },
  watch: {
    status () {
      this.reload()
    }
  },
  methods: {
    async load (offset) {
      this.loading = true
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/maxartifacts.json?status=' + this.status + '&limit=' + PAGE_SIZE + '&offset=' + offset + '&apikey=' + this.apiKey)
      } catch (e) {
        this.loading = false
        return this.showError(e)
      }
      this.loading = false
      const rows = Array.isArray(response.data) ? response.data : []
      this.artifacts = offset ? [...this.artifacts, ...rows] : rows
      this.hasMore = rows.length === PAGE_SIZE
    },
    reload () {
      return this.load(0)
    },
    loadMore () {
      return this.load(this.artifacts.length)
    },
    kindLabel (kind) {
      return this.maxLabels.kind[kind] || this.maxLabels.kind.custom
    },
    severityChips (artifact) {
      const counts = artifact.severity_counts || {}
      return SEVERITIES
        .filter(severity => severity !== 'info' && counts[severity] > 0)
        .map(severity => ({
          severity,
          count: counts[severity],
          label: this.maxLabels.severity[severity],
          color: this.severityColors[severity]
        }))
    },
    openArtifact (artifact) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'MaxArtifactDetails',
        title: artifact.title,
        width: 8,
        data: {
          artifactId: artifact.id
        }
      })
    }
  },
  created () {
    this.reload()
  },
  mounted () {
    if (!this.events) {
      this.events = {
        maxArtifactUpdate: () => this.reload()
      }
    }
    this.enableEventBus()
  }
}
</script>

<style scoped>
.max-artifact-row {
  display: grid;
  gap: 4px;
  width: 100%;
  text-align: left;
}
.max-artifact-row--unread {
  border-left: 3px solid #337ab7;
}
.max-artifact-row__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}
.max-artifact-row__title {
  text-wrap: pretty;
}
.max-artifact-row__meta {
  font-size: 12px;
}
.max-artifact-row__summary {
  text-wrap: pretty;
}
.max-artifact-row__severity {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}
.max-artifact-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.max-artifact-chip__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
</style>
